import React, { useEffect, useMemo, useState, useCallback, memo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Button, Drawer, Grid, Layout, Menu, Select, Typography, Tooltip, Badge } from 'antd';
import {
  GlobalOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  MoonOutlined,
  SunOutlined,
  BellOutlined
} from '@ant-design/icons';
import { authAPI } from '../services/api';
import { buildAdminMenuItems, getAdminRouteMeta } from '../appRoutes';
import { clearAuthStorage } from '../utils/auth';
import { getStoredZoneId, setStoredZoneId } from '../utils/zone';

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;
const { Text } = Typography;

/**
 * 重新设计的 MainLayout 组件
 * - 更现代的侧边栏设计
 * - 更好的响应式处理
 * - 优化的视觉层次
 */
const MainLayout = memo(function MainLayout({ themeMode = 'light', onToggleThemeMode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [zones, setZones] = useState([]);
  const [currentZoneId, setCurrentZoneId] = useState(getStoredZoneId());
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const isDarkMode = themeMode === 'dark';
  const menuItems = useMemo(() => buildAdminMenuItems(), []);
  const routeMeta = useMemo(() => getAdminRouteMeta(location.pathname), [location.pathname]);
  const selectedKeys = useMemo(() => [location.pathname], [location.pathname]);

  // 加载区服列表
  useEffect(() => {
    let mounted = true;

    const loadZones = async () => {
      try {
        const response = await authAPI.getZones();
        const payload = response.data?.data || {};
        const nextZones = Array.isArray(payload.zones) ? payload.zones : [];
        const preferredZoneId = getStoredZoneId() || payload.currentZoneId || payload.defaultZoneId || nextZones[0]?.id || '';

        if (!mounted) return;

        setZones(nextZones);
        setCurrentZoneId(preferredZoneId);
        if (preferredZoneId) {
          setStoredZoneId(preferredZoneId);
        }
      } catch (error) {
        if (!mounted) return;
        setZones([]);
      }
    };

    loadZones();

    return () => {
      mounted = false;
    };
  }, []);

  // 路由变化时关闭移动端抽屉
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // 退出登录
  const handleLogout = useCallback(() => {
    clearAuthStorage();
    navigate('/login', { replace: true });
  }, [navigate]);

  // 区服切换
  const handleZoneChange = useCallback((zoneId) => {
    setCurrentZoneId(zoneId);
    setStoredZoneId(zoneId);
    window.location.reload();
  }, []);

  // 切换导航：桌面折叠侧栏，移动端打开抽屉
  const handleMenuToggle = useCallback(() => {
    if (isMobile) {
      setDrawerOpen(true);
      return;
    }
    setCollapsed(prev => !prev);
  }, [isMobile]);

  // 品牌区域
  const brandNode = (
    <div className="app-brand">
      <div className="app-brand-mark">DA</div>
      <div className="app-brand-text">
        <span className="app-brand-title">
          {collapsed && !isMobile ? 'DA' : '旭日之战'}
        </span>
        <span className="app-brand-subtitle">
          {collapsed && !isMobile ? '' : '后台管理'}
        </span>
      </div>
    </div>
  );

  // 导航菜单
  const navigationMenu = (
    <Menu
      theme={isDarkMode ? 'dark' : 'light'}
      mode="inline"
      selectedKeys={selectedKeys}
      items={menuItems}
      onClick={({ key }) => navigate(key)}
      style={{ borderRight: 0 }}
    />
  );

  return (
    <Layout className="app-layout">
      {/* 桌面端侧边栏 */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={260}
          collapsedWidth={80}
          className="app-sider"
        >
          {brandNode}
          <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
            {navigationMenu}
          </div>
        </Sider>
      )}

      <Layout className="app-main">
        {/* 顶部导航 */}
        <Header className={`app-header${isMobile ? ' app-header-mobile' : ''}`}>
          {/* 左侧区域：菜单切换 + 页面标题 */}
          <div className="app-header-left">
            {/* 菜单切换按钮 */}
            <Tooltip title={isMobile ? '打开菜单' : (collapsed ? '展开菜单' : '收起菜单')} placement="bottom">
              <Button
                type="text"
                icon={isMobile || collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={handleMenuToggle}
                className="app-header-trigger"
              />
            </Tooltip>

            {/* 页面标题 */}
            <div className="app-page-header">
              <Text className="app-page-eyebrow">{routeMeta?.section || '管理中心'}</Text>
              <Text strong className="app-page-title">{routeMeta?.label || '控制台'}</Text>
            </div>
          </div>

          {/* 右侧区域：区服选择 + 通知 + 用户 */}
          <div className="app-header-right">
            {/* 区服选择 */}
            {zones.length > 0 && (
              <div className="app-zone-selector">
                <GlobalOutlined className="app-zone-icon" />
                <Select
                  value={currentZoneId || undefined}
                  onChange={handleZoneChange}
                  className="app-zone-select"
                  popupMatchSelectWidth={false}
                  options={zones.map((zone) => ({
                    value: zone.id,
                    label: zone.label || zone.name || zone.id
                  }))}
                  placeholder="选择区服"
                  style={{ width: 160 }}
                />
              </div>
            )}

            {/* 通知按钮 */}
            {/* Theme switch */}
            <Tooltip title={isDarkMode ? '切换浅色模式' : '切换深色模式'}>
              <Button
                type="text"
                aria-label={isDarkMode ? '切换浅色模式' : '切换深色模式'}
                icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />}
                onClick={onToggleThemeMode}
                className={`app-header-action-btn app-theme-toggle${isDarkMode ? ' app-theme-toggle-active' : ''}`}
              />
            </Tooltip>

            <Tooltip title="暂无新通知">
              <Badge count={0} size="small">
                <Button
                  type="text"
                  icon={<BellOutlined />}
                  className="app-header-action-btn"
                />
              </Badge>
            </Tooltip>

            {/* 用户信息 */}
            <div className="app-user-info">
              <Avatar
                size={36}
                icon={<UserOutlined />}
                style={{
                  background: 'var(--color-primary)',
                  cursor: 'pointer'
                }}
              />
              <div className="app-user-details">
                <Text strong className="app-user-name">管理员</Text>
                <Text type="secondary" className="app-user-role">系统管理员</Text>
              </div>
            </div>

            {/* 退出按钮 */}
            <Tooltip title="退出登录">
              <Button
                type="text"
                danger
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                className="app-header-action-btn"
              />
            </Tooltip>
          </div>
        </Header>

        {/* 内容区域 */}
        <Content className="app-content">
          <div className="app-workspace">
            <Outlet />
          </div>
        </Content>
      </Layout>

      {/* 移动端抽屉菜单 */}
      {isMobile && (
        <Drawer
          title={null}
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={280}
          closable={false}
          styles={{
            body: { padding: 0, display: 'flex', flexDirection: 'column' }
          }}
        >
          {brandNode}
          <div style={{ flex: 1, overflow: 'auto' }}>
            {navigationMenu}
          </div>
          <div style={{ padding: '16px', borderTop: '1px solid var(--line-soft)' }}>
            <Button
              type="text"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              block
            >
              退出登录
            </Button>
          </div>
        </Drawer>
      )}
    </Layout>
  );
});

export default MainLayout;
